import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { openPopupAction } from 'editor/src/store/app/slice';
import requestDesignDataOperation from 'editor/src/store/design/operation/requestDesignDataOperation';
import setLoadingWithTimeoutOperation from 'editor/src/store/editor/operation/setLoadingWithTimeoutOperation';
import getIsLoading from 'editor/src/store/editor/selector/getIsLoading';
import { WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import getECommerceProduct from 'editor/src/store/variants/selector/getECommerceProduct';

import { getPersonalizationPricingSession } from 'editor/src/util/personalizationPricing/runtime';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import { PopupName } from '../Popup/popups';

import getCurrentProductVariantPrice from './getCurrentProductVariantPrice';
import getExitWarningPopupData from './getExitWarningPopupData';

function useEcommerceHeader() {
  const dispatch = useDispatch();
  const store = useStore();
  const personalizationPricingSession = getPersonalizationPricingSession(store);
  const isEditorHeaderEnabled = useSelector((state) => getHostSetting(state, 'enableEditorHeader'));
  const isEnableManualSaveDraft = useSelector((state) => getHostSetting(state, 'enableManualSaveDraft'));

  const { t } = useTranslation();
  const editorMode = useSelector((state) => getHostSetting(state, 'editorMode'));
  const isLoading = useSelector(getIsLoading);

  const currentProduct = useSelector(getECommerceProduct);
  const legacyProductPrice = useMemo(() => getCurrentProductVariantPrice(currentProduct), [currentProduct]);
  const [pricingState, setPricingState] = useState(personalizationPricingSession.getState());
  // Held only to re-render when the host answers; `canCheckout` below is what reads the match.
  const [, setPricingMatch] = useState(personalizationPricingSession.getMatch());
  useEffect(() => personalizationPricingSession.subscribe(setPricingState), [personalizationPricingSession]);
  useEffect(() => personalizationPricingSession.subscribeMatch(setPricingMatch), [personalizationPricingSession]);
  const [hostPrice, setHostPrice] = useState(personalizationPricingSession.getPrice());
  useEffect(() => personalizationPricingSession.subscribePrice(setHostPrice), [personalizationPricingSession]);
  const isPricingBlocked = !personalizationPricingSession.canCheckout();
  // With print-area pricing the host owns the price: the base variant's would be wrong the moment an
  // area is added, so the header shows what the host says this design costs and nothing until it does.
  const productPrice =
    pricingState.status === 'disabled'
      ? legacyProductPrice
      : { price: hostPrice ?? undefined, originalPrice: undefined };

  const handleHeaderBackButtonClick = useCallback(() => {
    if (!editorMode) {
      sendPostMessage('shopify.backButtonClick', undefined);
      dispatch(setLoadingWithTimeoutOperation());
      return;
    }

    const data = getExitWarningPopupData({ editorMode, t });

    dispatch(
      openPopupAction({
        open: PopupName.CONFIRMATION_POPUP,
        data,
      }),
    );
  }, [editorMode]);

  const handleHeaderContinueButtonClick = useCallback(() => {
    if (!personalizationPricingSession.canCheckout()) {
      return;
    }
    // A charged print area left empty is a cheaper purchase, not a mistake, and the session already
    // refuses a design that fills none. An empty image box sits inside an area the buyer did start,
    // so it stays a mistake either way.
    const pricingActive = personalizationPricingSession.getState().status !== 'disabled';
    const emptyAreaWarnings = pricingActive ? [] : [{ type: WarningType.EmptyPage, level: [WarningLevel.Medium] }];
    dispatch(
      requestDesignDataOperation({
        data: {
          checkWarnings: [
            ...emptyAreaWarnings,
            { type: WarningType.EmptyDesign, level: [WarningLevel.Medium] },
            { type: WarningType.EmptyImageBox, level: [WarningLevel.Medium] },
            { type: WarningType.LowDPI, level: [WarningLevel.High] },
            { type: WarningType.SampleElementNotReplaced, level: [WarningLevel.High] },
            { type: WarningType.NotVisible, level: [WarningLevel.Medium] },
            { type: WarningType.OutOfRange, level: [WarningLevel.Medium] },
            { type: WarningType.DtfImageBackground, level: [WarningLevel.Low] },
          ],
          setIsLoadingOnConfirm: true,
        },
        callback: { type: 'shopify.proceedButtonClick' },
      }),
    );
  }, []);

  return {
    isEditorHeaderEnabled,
    isEnableManualSaveDraft,
    handleHeaderBackButtonClick,
    handleHeaderContinueButtonClick,
    productPrice,
    isLoading,
    isPricingBlocked,
  };
}

export default useEcommerceHeader;
